import styles from './index.module.css'

import type { CellContext } from '@tanstack/react-table'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { COLORS } from '@disco/utils/color-indicators'

import type { Track } from 'types/track'

import { Text, Tooltip } from 'components/ds'

import { indicatorColorClass } from './utils/indicator-colors'
import { useIndicatorFilter } from './utils/indicator-filter-context'
import { OverflowHoverCard } from './components/overflow-hover-card'
import type { ColoredTag } from './components/tag-dot'
import { TagDot } from './components/tag-dot'

/** Default width of the indicators column, shared with its column config. */
export const INDICATORS_COLUMN_DEFAULT_SIZE = 120

// Dots shown at the default column width; extra dots only appear past the
// expand threshold so a normal-width column stays a compact three-dot cluster.
const BASE_VISIBLE = 3
// Inner width beyond which the cell starts revealing more than BASE_VISIBLE dots.
// Set to the column's default size so a resting column stays a compact cluster
// and dots only appear once the user drags the column wider.
const EXPAND_MIN_WIDTH = INDICATORS_COLUMN_DEFAULT_SIZE
// Extra inner width that reveals one more dot. Dots are only ~11px each, so a
// pure fit calculation would surface them all at once; this paces the reveal to
// the drag distance instead (one dot per WIDTH_PER_EXTRA_DOT of expansion).
const WIDTH_PER_EXTRA_DOT = 20

// How many dots to show for the given inner width: BASE_VISIBLE until the column
// is dragged past EXPAND_MIN_WIDTH, then one more for each WIDTH_PER_EXTRA_DOT of
// additional width.
const fitVisibleCount = (width: number, total: number): number => {
  const base = Math.min(total, BASE_VISIBLE)
  if (total <= BASE_VISIBLE || width <= EXPAND_MIN_WIDTH) return base

  const extra = Math.floor((width - EXPAND_MIN_WIDTH) / WIDTH_PER_EXTRA_DOT)
  return Math.min(total, base + extra)
}

type Props = { info: CellContext<Track, unknown> }

const IndicatorsColumnComponent = ({ info }: Props) => {
  const { activeTagIds, filterByTag } = useIndicatorFilter()
  const containerRef = useRef<HTMLDivElement>(null)
  const rawTags = info.row.original.native_tags

  const coloredTags = useMemo<ColoredTag[]>(
    () =>
      (rawTags ?? []).filter(
        (tag): tag is ColoredTag =>
          typeof tag.color === 'string' && Boolean(COLORS[tag.color]),
      ),
    [rawTags],
  )
  const totalTags = coloredTags.length

  const [visibleCount, setVisibleCount] = useState(() =>
    fitVisibleCount(info.column?.getSize() ?? 0, totalTags),
  )

  useEffect(() => {
    const element = containerRef.current
    // Width only affects the cell once there are dots to overflow, so most
    // cells never pay for an observer.
    if (!element || totalTags <= BASE_VISIBLE) return

    // Storing the derived count rather than the raw width lets setState bail
    // out on unchanged values, so a column drag only re-renders the cell when
    // it crosses a reveal threshold instead of on every pixel.
    const update = (width: number) => {
      if (width > 0) setVisibleCount(fitVisibleCount(width, totalTags))
    }

    update(element.getBoundingClientRect().width)

    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width
      if (width !== undefined) update(width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [totalTags])

  // The cell sits inside a row-wide click target that selects the track, so a
  // filter click must not also change the selection.
  const handleTagClick = useCallback(
    (event: React.MouseEvent, tagId: number) => {
      event.stopPropagation()
      filterByTag(tagId)
    },
    [filterByTag],
  )

  const activeIdSet = useMemo(() => new Set(activeTagIds), [activeTagIds])

  // Filtered tags lead the cell so the active indicators line up down the
  // column, and so an active tag can never be buried in the overflow badge.
  const orderedTags = useMemo(() => {
    if (activeIdSet.size === 0) return coloredTags
    return [
      ...coloredTags.filter(tag => activeIdSet.has(tag.id)),
      ...coloredTags.filter(tag => !activeIdSet.has(tag.id)),
    ]
  }, [activeIdSet, coloredTags])

  if (totalTags === 0) {
    return (
      <Text
        size="2"
        color="gray"
        className={styles.plainValue}
        data-testid="indicators-cell"
      >
        —
      </Text>
    )
  }

  const visible = orderedTags.slice(0, visibleCount)
  const hidden = orderedTags.slice(visible.length)

  return (
    <div
      ref={containerRef}
      className={styles.container}
      data-testid="indicators-cell"
    >
      <span className={styles.dots}>
        {visible.map((tag, index) => {
          const isActive = activeIdSet.has(tag.id)
          return (
            <Tooltip
              key={tag.id}
              content={
                <span className={styles.tooltipContent}>
                  <span>{isActive ? 'Clear filter:' : 'Filter by:'}</span>
                  <span className={styles.tooltipTag}>
                    <TagDot color={tag.color} />
                    {tag.name}
                  </span>
                </span>
              }
              side="top"
            >
              <button
                type="button"
                className={`${styles.dot} ${indicatorColorClass(tag.color)}`}
                data-testid="indicator-dot"
                data-active={isActive || undefined}
                aria-pressed={isActive}
                aria-label={`Filter by ${tag.name}`}
                onClick={event => handleTagClick(event, tag.id)}
                // zIndex keeps the cascade (leftmost dot on top) working for any
                // number of dots, unlike a fixed nth-child rule.
                style={{ zIndex: visible.length - index }}
              />
            </Tooltip>
          )
        })}
      </span>
      {hidden.length > 0 && (
        <OverflowHoverCard
          tags={orderedTags}
          hiddenCount={hidden.length}
          activeIdSet={activeIdSet}
          onTagClick={handleTagClick}
        />
      )}
    </div>
  )
}

export const IndicatorsColumn = React.memo(IndicatorsColumnComponent)
IndicatorsColumn.displayName = 'IndicatorsColumn'
